import { createFileRoute, Link } from '@tanstack/react-router'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useState, type ReactNode } from 'react'
import { z } from 'zod'
import { createClient } from '@kairos/supabase/browser'

import { adminAuthRedirect } from '~/lib/auth/auth-redirect'
import {
  ErrorBanner,
  Field,
  Stack,
  StatusCard,
  SubmitButton,
} from '~/components/auth/auth-ui'
import {
  Turnstile,
  getTurnstileSiteKey,
} from '~/components/auth/Turnstile'
import { logger } from '~/lib/logger'

/**
 * `/authentication/forgot-password` — recovery sub-surface 3 from
 * `02-authentication.md`. Two visual variants:
 *
 *  - `form` (default) — email field + Send reset link primary.
 *  - `sent`           — calm success card "Check your email".
 *
 * The variant flips automatically once the request resolves. To preview the
 * sent state during design review, append `?state=sent` (matches the
 * prototype's debug pattern in `kairos-connect-admin-redesign/.../auth.html`).
 *
 * Per spec: we DO NOT reveal whether an email is registered. The same `sent`
 * card renders regardless of Supabase's response, so the silent path holds.
 */

const searchSchema = z.object({
  state: z.enum(['form', 'sent']).optional(),
})

const formSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email'),
})

type FormValues = z.infer<typeof formSchema>

export const Route = createFileRoute('/authentication/forgot-password')({
  validateSearch: searchSchema,
  component: ForgotPasswordPage,
})

export function ForgotPasswordPage(): ReactNode {
  const search = Route.useSearch()
  const [variant, setVariant] = useState<'form' | 'sent'>(search.state ?? 'form')
  const [submittedEmail, setSubmittedEmail] = useState<string>('')
  const [bannerError, setBannerError] = useState<string | null>(null)

  if (variant === 'sent') {
    return (
      <StatusCard
        tone="success"
        glyph="✓"
        title="Check your email"
        body={
          <>
            We sent a reset link to{' '}
            <strong className="ka-auth-strong">{submittedEmail || 'your inbox'}</strong>. It expires in
            30 minutes.
          </>
        }
      >
        <button
          type="button"
          onClick={() => setVariant('form')}
          className="ka-link"
        >
          Try a different email
        </button>
      </StatusCard>
    )
  }

  return (
    <ResetRequestForm
      bannerError={bannerError}
      onError={setBannerError}
      onSent={(email) => {
        setSubmittedEmail(email)
        setVariant('sent')
      }}
    />
  )
}

interface ResetRequestFormProps {
  readonly bannerError: string | null
  readonly onError: (message: string | null) => void
  readonly onSent: (email: string) => void
}

// Exported for the route's unit tests; rendered by `ForgotPasswordPage` above.
export function ResetRequestForm({
  bannerError,
  onError,
  onSent,
}: ResetRequestFormProps): ReactNode {
  // CAPTCHA outage hotfix: Supabase CAPTCHA protection is enabled project-wide,
  // so GoTrue gates password-reset requests too and rejects them with
  // `captcha protection: request disallowed (no captcha_token found)` unless a
  // solved Turnstile token is forwarded. Same pattern as `authentication.login`
  // (PR #1320). With no site key configured the widget is absent and nothing
  // changes.
  const turnstileSiteKey = getTurnstileSiteKey()
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  // Remount key: a Turnstile token is single-use, so after GoTrue consumes one
  // the widget must hand out a fresh challenge.
  const [captchaAttempt, setCaptchaAttempt] = useState(0)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: '' },
  })

  async function onSubmit(values: FormValues): Promise<void> {
    onError(null)

    // Never fire a request that cannot pass: when the widget is rendered the
    // token must exist first, and the refusal is surfaced in the error banner
    // rather than silently doing nothing.
    if (turnstileSiteKey && !captchaToken) {
      onError('Complete the verification challenge to continue.')
      return
    }

    try {
      const supabase = createClient()
      // CASA 2.4: the allow-listed reset page, never `window.location.origin`
      // (the dev server / preview host is not in the Supabase allow-list).
      const { error } = await supabase.auth.resetPasswordForEmail(values.email, {
        redirectTo: adminAuthRedirect('password-reset'),
        // GoTrue performs the siteverify; the browser only forwards the token.
        ...(captchaToken ? { captchaToken } : {}),
      })
      if (error) {
        // A Turnstile token is single-use, so clear the spent one and remount
        // the widget to issue a fresh challenge before any retry.
        if (captchaToken) {
          setCaptchaToken(null)
          setCaptchaAttempt((attempt) => attempt + 1)
        }
        // Per spec: do NOT reveal whether the email is registered. Only surface
        // structural failures (rate-limit) to the user.
        if (error.status === 429) {
          onError('Too many requests. Try again in a few minutes.')
          return
        }
        logger.warn('forgot-password supabase error', {
          status: error.status,
          message: error.message,
        })
      }
      onSent(values.email)
    } catch (cause: unknown) {
      logger.warn('forgot-password unexpected error', {
        message: cause instanceof Error ? cause.message : 'unknown',
      })
      // Still report success — silent path. Surfacing internal errors here
      // would reveal whether the email exists.
      onSent(values.email)
    }
  }

  return (
    <Stack>
      <header>
        <h1 className="ka-auth-h1">Reset your password</h1>
        <p className="ka-auth-sub">We&rsquo;ll send a reset link to your email.</p>
      </header>

      {bannerError ? <ErrorBanner message={bannerError} /> : null}

      <form noValidate onSubmit={handleSubmit(onSubmit)} method="post" className="ka-stack">
        <Field
          id="email"
          label="Email"
          type="email"
          placeholder="you@kairos.com"
          autoComplete="email"
          error={errors.email?.message}
          {...register('email')}
        />
        {turnstileSiteKey ? (
          <Turnstile
            key={captchaAttempt}
            siteKey={turnstileSiteKey}
            onVerify={setCaptchaToken}
            onExpire={() => setCaptchaToken(null)}
            onError={() => setCaptchaToken(null)}
          />
        ) : null}
        <SubmitButton loading={isSubmitting} label="Send reset link" loadingLabel="Sending…" />
      </form>

      <p className="ka-field-helper ka-auth-text-center">
        Remembered it?{' '}
        <Link to="/authentication/login" className="ka-link">
          Back to sign in
        </Link>
      </p>
    </Stack>
  )
}
